import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import { Radio, RadioGroup } from "@citi-icg-172888/icgds-react";
import { Card } from "@citi-icg-172888/icgds-react/Card";
import FooterButtonHoc from "./common/FooterButtonHoc";
import CommonHeader from "./external/CommonHeader";
import { useData } from "./services/context/DataContext";
import { GET_LEAD_DATA_URL } from "./services/api/apiUrls";
import { handleScript } from "./services/utils/utils";

interface AknowledgeScreenProps {
  onAcknowledge: (item?: string) => void;
}

const AknowledgeScreen: React.FC<AknowledgeScreenProps> = ({ onAcknowledge }) => {
  const [selection, setSelection] = useState<string>("");
  const [showTitleOnly, setShowTitleOnly] = useState<boolean>(false);
  const { isCardOpen, setUrlFetch, setIsCardOpen } = useData();
  const headerRef = useRef<HTMLHeadingElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const divMainRef = useRef<HTMLDivElement>(null);

  const handleAknowledge = () => {
    handleScript(divMainRef, "ccbContactUsFormAcknowledge");

    // Move focus to the first logical element in the updated content
    const updatedContent = document.getElementById("no_text") || divMainRef.current;
    if (updatedContent) {
      updatedContent.focus();
    }
  };

  useEffect(() => {
    if (window.parent.location.href.includes("contact-us")) {
      divRef.current?.focus();
      if (window.innerWidth < 768) {
        window.scroll(0, 0);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, []);

  useEffect(() => {
    if (isCardOpen) {
      const radioGroup = document.querySelectorAll(".lmn-selection-control");

      radioGroup.forEach((marker) => {
        marker.setAttribute("tabindex", "0");
        marker.addEventListener("keydown", (event: any) => {
          const options = ["Yes", "No"];
          const currentIndex = options.indexOf(event.target.textContent);
          if (event.key === "Enter" && event.target.textContent) {
            setSelection(event.target.textContent);
            const mockEvent = {
              target: {
                value: event.target.textContent, // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          } else if (event.key === "ArrowUp" && event.target.textContent) {
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
            setSelection(options[prevIndex]);
            const prevMarker = radioGroup[prevIndex] as HTMLInputElement;
            if (prevMarker) {
              prevMarker.focus();
            }
            const mockEvent = {
              target: {
                value: options[prevIndex], // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          } else if (event.key === "ArrowDown" && event.target.textContent) {
            const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : currentIndex;
            setSelection(options[nextIndex]);
            const nextMarker = radioGroup[nextIndex] as HTMLInputElement;
            if (nextMarker) {
              nextMarker.focus();
            }
            const mockEvent = {
              target: {
                value: options[nextIndex], // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          }
        });
      });
    }
  }, [isCardOpen]);

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value === "No") {
      setUrlFetch(GET_LEAD_DATA_URL);
    }
    setSelection(value);
  };

  const toggleContent = () => {
    setShowTitleOnly(!showTitleOnly);
    setSelection("");
    if (window.parent.location.href.includes("contact-us")) window.scrollTo(0, 0);
  };

  const toggleCardVisible = () => {
    onAcknowledge("acknowledge");
  };

  const toggleCardVisibility = () => {
    setIsCardOpen(!isCardOpen);
  };

  return (
    <>
      <Card
        className="ccbDncaWrapper lmn-container content-container lmn-dnca-w-xl-60 lmn-dnca-xl-p-9"
        role="main"
      >
        <Card id="no-border" className="ccbDncaWrapper lmn-container lmn-dnca-w-xl-95 no-border">
          <div className="ccbDncaWrapper lmn-container margin-header">
            <CommonHeader toggleCardVisibility={toggleCardVisibility} ref={headerRef} />
          </div>
          <div ref={divMainRef}></div>
          {isCardOpen && (
            <>
              <Card id="label_acknowledge">
                <div
                  ref={divRef}
                  className="lmn-card-title-text lmn-mb-6 lmn-body-2-multi lmn-text-left label-text"
                  id="to_help_text"
                  role="region"
                  aria-labelledby="help_text_label"
                >
                  <h3 id="help_text_label" role="heading" aria-level={3} className="lmn-text-color-desc lmn-card-title-text">
                    To help us put you in contact with the right person,
                    <br /> Please answer the following questions
                  </h3>
                </div>
                <div
                  className="lmn-form-group lmn-dnca-w-m-100 lmn-dnca-w-s-100 lmn-dnca-w-xs-95 lmn-mr-5 lmn-mb-6 label-text"
                  id="existing_question"
                >
                  <label
                    className="lmn-card-title-text-label lmn-body-5-multi lmn-required"
                    aria-label="Are you an existing client with us?"
                  >
                    Are you an existing client with us?
                  </label>
                  <RadioGroup
                    name="selection-existing-client"
                    value={selection}
                    onChange={onChangeHandler}
                  >
                    <Radio value="Yes" aria-label="Are you an existing client with us? Yes">
                      Yes
                    </Radio>
                    <Radio value="No" aria-label="Are you an existing client with us? No">
                      No
                    </Radio>
                  </RadioGroup>
                </div>
                <div>
                  {selection === "Yes" && (
                    <div>
                      <div
                        className="lmn-card-title-text-desc lmn-mb-6 lmn-body-3-single lmn-text-left label-text"
                        id="yes_text"
                      >
                        The quickest way for you to get in contact with the Commercial Bank is to
                        reach out to your Relationship Manager or Service Representative directly.
                        Alternatively, click{" "}
                        <a
                          id="yes_here_link"
                          className="lmn-card-title-text lmn-mb-6 lmn-body-3-single lmn-text-left"
                          href="https://online.citi.com/US/ag/contactus"
                          target="_blank"
                          style={{
                            textDecoration: "underline",
                          }}
                          tabIndex={0}
                          aria-label="Click here to see a list of contact information for other business lines within Citi."
                        >
                          here
                        </a>{" "}
                        to see a list of contact information for other business lines within Citi.
                      </div>
                      <div className="lmn-card-title-text-desc lmn-mb-6 lmn-body-3-single lmn-text-left">
                        Read{" "}
                        <a
                          className="lmn-card-title-text-desc lmn-mb-6 lmn-body-3-single lmn-text-left"
                          href="https://www.citigroup.com/rcs/citigpa/storage/public/global-privacy-policy-institutional-clients.pdf"
                          target="_blank"
                          style={{ textDecoration: "underline" }}
                          tabIndex={0}
                          id="yes_global_note_link"
                        >
                          Global Privacy Notice for Institutional Clients
                        </a>
                      </div>
                      <div className="lmn-dnca-w-xs-100">
                        <FooterButtonHoc
                          submitText="Close"
                          showDivider={false}
                          closeSubmission={true}
                          onSubmit={toggleContent}
                        />
                      </div>
                    </div>
                  )}
                  {selection === "No" && (
                    <div>
                      <div id="no_text" className="lmn-card-title-text-desc lmn-mb-6 label-text">
                        Thank you for your interest in Citi Commercial Bank. We aim to be the bank
                        of choice for fast growing companies, helping them simplify the challenges
                        of rapid scale and doing business abroad. We offer a holistic banking
                        relationship that covers the wide spectrum of financial solutions from
                        credit to cash management and FX to wealth.
                        <br />
                        <br /> The following questions are structured to find out a little more
                        about your company and its objectives.
                      </div>
                      <div className="lmn-card-title-text lmn-mb-6 lmn-body-3-single lmn-text-left">
                        Read{" "}
                        <a
                          id="no_global_note_link"
                          className="lmn-card-title-text lmn-mb-6 lmn-body-3-single lmn-text-left"
                          href="https://www.citigroup.com/rcs/citigpa/storage/public/global-privacy-policy-institutional-clients.pdf"
                          style={{ textDecoration: "underline" }}
                          target="_blank"
                          tabIndex={0}
                        >
                          Global Privacy Notice for Institutional Clients
                        </a>
                      </div>

                      <div className="lmn-dnca-w-xs-100">
                        <FooterButtonHoc
                          backText="Back"
                          submitText="Acknowledge"
                          showDivider={false}
                          closeSubmission={true}
                          onBack={toggleContent}
                          onSubmit={() => {
                            toggleCardVisible();
                            handleAknowledge();
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </>
          )}
        </Card>
      </Card>
    </>
  );
};

export default AknowledgeScreen;
